/**
 * Геометрия сетки дня: минуты → пиксели.
 *
 * Здесь нет ни React, ни данных сервера — только числа. Это сделано нарочно: плотность
 * сетки и есть главное требование к журналу (ТЗ §2.0), и проверять её надо расчётом,
 * а не глазами по снимку экрана. Все значения ниже взяты из tokens.json (раздел grid)
 * и из макета «Кабинет - этап 0.dc.html», который назван эталоном плотности.
 *
 * ПОЧЕМУ ЧИСЛА ПОВТОРЕНЫ ЗДЕСЬ, А НЕ ЧИТАЮТСЯ ИЗ CSS. Высоту карточки и высоту дня
 * считает код (от них зависит, что поместится в карточку), а рисует CSS. Читать
 * getComputedStyle на каждую карточку — это принудительный пересчёт вёрстки в цикле
 * по тысяче элементов. Поэтому число живёт в двух местах, и оба обязаны сойтись:
 * сторож grid-tokens.test.ts сверяет константы этого файла с переменными tokens.css.
 */

/** Ширина шкалы времени слева. Она закреплена и не уезжает при прокрутке. */
export const GRID_TIME_COLUMN = 56
/** Высота шапки с мастерами. Закреплена сверху. */
export const GRID_MASTER_HEADER = 44
/** Колонка «Без мастера». Присутствует всегда, даже пустая (ТЗ §2.2.2). */
export const GRID_UNASSIGNED_COLUMN = 140
export const GRID_MIN_COLUMN = 150
export const GRID_MAX_COLUMN = 300

/** Минимальная читаемая карточка: имя клиента и значок статуса. */
export const GRID_MIN_CARD = 20
/** С этой высоты в карточку добавляется строка времени. */
export const GRID_CARD_TIME = 34
/** С этой высоты добавляется название услуги. */
export const GRID_CARD_SERVICE = 48
/**
 * Ниже этой высоты карточка не рисуется вовсе, даже если её время короче.
 * Карточка «перерастает» своё время до 20 px, но не закрывает начало следующей записи —
 * и вот это число ограничивает перерастание снизу, когда закрывать нечего (макет).
 */
export const GRID_CARD_FLOOR = 9

/** Подпись времени — раз в 30 минут при любом шаге (ТЗ §2.2.1.1). */
export const GRID_LABEL_EVERY = 30
/** При шаге мельче 15 линии рисуются только получасовые, иначе сетка превращается в рябь. */
export const GRID_LINE_EVERY_BELOW_STEP_15 = 30

/** Опорные высоты строки из tokens.json → grid.rowHeight. Вариант плотности B. */
const ROW_HEIGHT_STEP_5 = 12
const ROW_HEIGHT_STEP_15 = 26
const ROW_HEIGHT_STEP_30 = 26

/**
 * Высота строки для шага сетки.
 *
 * Салон выбирает шаг из шести значений — 5, 10, 15, 20, 30, 60 (решение владельца
 * от 16.09.2026), а дизайнер сдал три опорных: 5, 15 и 30. Поэтому опорные берутся
 * из токенов как есть, а промежуточные выводятся правилом:
 *
 * - между 5 и 15 высота строки растёт линейно от 12 к 26 — плотность меняется плавно,
 *   и шаг 10 не оказывается ни рябью пятиминутной сетки, ни разрежённостью;
 * - между 15 и 30 оба токена дают 26, поэтому и всё, что между ними, — 26;
 * - крупнее 30 плотность НЕ падает дальше: ТЗ прямо говорит, что шаг 60 — это
 *   тридцатиминутный макет с вдвое меньшим числом линий. Значит пикселей на минуту
 *   столько же, а строка вдвое выше.
 */
export function rowHeight(stepMinutes: number): number {
  if (stepMinutes <= 5) return ROW_HEIGHT_STEP_5
  if (stepMinutes < 15) {
    const ratio = (stepMinutes - 5) / 10
    return Math.round(ROW_HEIGHT_STEP_5 + (ROW_HEIGHT_STEP_15 - ROW_HEIGHT_STEP_5) * ratio)
  }
  if (stepMinutes <= 30) return ROW_HEIGHT_STEP_15
  return Math.round((stepMinutes * ROW_HEIGHT_STEP_30) / 30)
}

/** Пикселей на минуту. Единственный множитель, через который проходит вся сетка. */
export function pixelsPerMinute(stepMinutes: number): number {
  return rowHeight(stepMinutes) / stepMinutes
}

/** Отступ сверху для момента внутри окна дня. */
export function minuteToTop(minute: number, openMinute: number, stepMinutes: number): number {
  return Math.round((minute - openMinute) * pixelsPerMinute(stepMinutes))
}

/** Высота всего дня: от открытия до закрытия. */
export function gridHeight(openMinute: number, closeMinute: number, stepMinutes: number): number {
  return Math.round((closeMinute - openMinute) * pixelsPerMinute(stepMinutes))
}

/**
 * Ширина колонки мастера.
 *
 * Пол 150 и потолок 300 из токенов: ниже 150 имя мастера перестаёт читаться, выше 300
 * колонка начинает выглядеть пустой. Между ними — сколько влезло. Когда колонки упёрлись
 * в пол, появляется горизонтальная прокрутка, и это нормальный режим для 15 мастеров.
 *
 * ПОРОГ ПРОКРУТКИ НЕ ЗАДАН ЧИСЛОМ — он следует из пола колонки, и дизайнер подтвердил
 * то, что из него выходит (tokens.json → rules.sixMastersOn1366): шесть мастеров
 * на 1366 помещаются, прокрутка начинается с седьмого. Подпись в макете утверждала
 * обратное, и ошибочной признана подпись, а не числа. Закреплено обеими сторонами
 * порога в grid-geometry.test.ts: если прокрутка при шести всё же понадобится, менять
 * надо ширины колонок, а не заводить здесь отдельное число.
 */
export function columnWidth(availableWidth: number, columnCount: number): number {
  const perColumn = Math.floor(availableWidth / Math.max(columnCount, 1))
  return Math.max(GRID_MIN_COLUMN, Math.min(GRID_MAX_COLUMN, perColumn))
}

/** Полная ширина слоя: шкала + колонки + «Без мастера». */
export function totalWidth(width: number, columnCount: number): number {
  return GRID_TIME_COLUMN + width * columnCount + GRID_UNASSIGNED_COLUMN
}

/** Через сколько минут рисуется линия. Мельче 15 — только получасовые. */
export function lineEvery(stepMinutes: number): number {
  return stepMinutes < 15 ? GRID_LINE_EVERY_BELOW_STEP_15 : stepMinutes
}

/**
 * Вес линии. Три веса, и различаются они не только цветом: в проверке в градациях
 * серого час обязан остаться заметнее получаса, иначе по сетке нельзя вести глаз.
 */
export type GridLineWeight = "hour" | "half" | "minor"

export interface GridLine {
  readonly minute: number
  readonly top: number
  readonly weight: GridLineWeight
}

export function gridLines(
  openMinute: number,
  closeMinute: number,
  stepMinutes: number,
): readonly GridLine[] {
  const every = lineEvery(stepMinutes)
  const lines: GridLine[] = []
  // Отсчёт ведётся от полуночи, а не от открытия: иначе «час» перестанет попадать
  // на круглый час, стоит салону открыться в 9:30.
  const first = Math.ceil(openMinute / every) * every
  for (let minute = first; minute <= closeMinute; minute += every) {
    const weight: GridLineWeight = minute % 60 === 0 ? "hour" : minute % 30 === 0 ? "half" : "minor"
    lines.push({ minute, top: minuteToTop(minute, openMinute, stepMinutes), weight })
  }
  return lines
}

export interface GridLabel {
  readonly minute: number
  readonly top: number
  readonly text: string
  readonly isHour: boolean
  /** Самая первая подпись прижимается к верху, а не центрируется: ей некуда уехать. */
  readonly isFirst: boolean
}

/** «09:00». Минуты за пределами суток сворачиваются — ночная смена переходит через полночь. */
export function formatMinuteLabel(minute: number): string {
  const wrapped = ((minute % 1440) + 1440) % 1440
  const hours = Math.floor(wrapped / 60)
  const minutes = wrapped % 60
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
}

export function gridLabels(
  openMinute: number,
  closeMinute: number,
  stepMinutes: number,
): readonly GridLabel[] {
  const labels: GridLabel[] = []
  const first = Math.ceil(openMinute / GRID_LABEL_EVERY) * GRID_LABEL_EVERY
  // Подпись времени закрытия не ставится: под ней нет строки, которую она подписывала бы.
  for (let minute = first; minute < closeMinute; minute += GRID_LABEL_EVERY) {
    labels.push({
      minute,
      top: minuteToTop(minute, openMinute, stepMinutes),
      text: formatMinuteLabel(minute),
      isHour: minute % 60 === 0,
      isFirst: minute === openMinute,
    })
  }
  return labels
}

/**
 * Высота карточки записи и правило перерастания.
 *
 * Услуга на 10 минут — это реальная позиция прайса (коррекция бровей, подкраска корней),
 * и при шаге 15 она даёт 17 px, в которых не читается ничего. Поэтому карточка дорастает
 * до минимальных 20 px, но НЕ дальше начала следующей записи: карточка, молча закрывшая
 * соседнюю, — прямо запрещённый ТЗ §2.3.1 случай. Если закрывать нечего (следующей записи
 * нет), карточка просто получает свои 20 px.
 *
 * @param minutesToNext сколько минут от начала этой записи до начала следующей в колонке.
 */
export function cardHeight(
  durationMinutes: number,
  stepMinutes: number,
  minutesToNext: number | null,
): number {
  const perMinute = pixelsPerMinute(stepMinutes)
  const natural = Math.round(durationMinutes * perMinute)
  if (natural >= GRID_MIN_CARD) return natural

  const cap = minutesToNext === null ? GRID_MIN_CARD : Math.round(minutesToNext * perMinute) - 2
  return Math.max(Math.min(GRID_MIN_CARD, cap), GRID_CARD_FLOOR)
}

/** Что помещается в карточку такой высоты. Порядок отбрасывания снизу вверх — ТЗ §2.3.1. */
export type CardDetail = "name" | "time" | "service"

export function cardDetail(height: number): CardDetail {
  if (height >= GRID_CARD_SERVICE) return "service"
  if (height >= GRID_CARD_TIME) return "time"
  return "name"
}
